import { useCallback, useContext } from 'react';

import axiosApiInstance from '@/api/shared/axios-instance';
import { REDUCERS } from '@/constants/reducers';
import { Context } from '@/context';
import URL_FACTORY from '@/helpers/url-helper';
import { notifySuccess, notifyWarning } from '@/notifications/notify';
import { HttpStatusCode } from 'axios';
import useIsUserPremium from '@/DesignSystem/hooks/useIsUserPremium';
import useIsUserAuthenticated from '@/DesignSystem/hooks/userIsAuthenticated';

export default function useFavoritos() {
    const { state, dispatch } = useContext(Context);
    const isUserAuthenticated = useIsUserAuthenticated();
    const isUserPremium = useIsUserPremium();

    const setItems = (items) => dispatch({ type: REDUCERS.UPDATE_FAVORITOS, payload: { items } });

    // Verificar si el usuario está autenticado
    const obtenerFavoritos = useCallback(async () => {
        if (!isUserAuthenticated) {
            return [];
        }

        axiosApiInstance
            .get(URL_FACTORY.ObtenerFavoritos)
            .then((res) => {
                const list = res?.data?.items ?? res?.data ?? [];
                setItems(list);
                return list;
            })
            .catch((error) => {
                console.error('Error al obtener favoritos:', error);
                return [];
            });
    }, [isUserAuthenticated]);

    const agregarFavorito = useCallback(
        async (payload) => {
            // Verificar autenticación antes de agregar
            if (!isUserAuthenticated) {
                notifyWarning('Debes estar autenticado para agregar favoritos');
                return null;
            }

            if (state?.favoritos?.items?.length >= 5 && !isUserPremium) {
                notifyWarning('Debes ser usuario premium para agregar más favoritos');
                return null;
            }

            try {
                const url = URL_FACTORY.AgregarFavorito;
                const res = await axiosApiInstance.post(url, payload);
                const list = res?.data?.items ?? res?.data ?? [];
                setItems(list);
                notifySuccess('Jugador agregado a favoritos');
                return list;
            } catch (error) {
                if (error.response?.status === HttpStatusCode.PaymentRequired) {
                    notifyWarning('Debes ser usuario premium para agregar más favoritos');
                    return null;
                } else {
                    console.error('Error al agregar favorito:', error);
                    throw error;
                }
            }
        },
        [isUserAuthenticated, state?.favoritos?.items, isUserPremium]
    );

    const eliminarFavorito = useCallback(
        async (payload) => {
            // Verificar autenticación antes de eliminar
            if (!isUserAuthenticated) {
                notifyWarning('Debes estar autenticado para gestionar favoritos');
                return null;
            }

            try {
                const url = URL_FACTORY.EliminarFavorito;
                const res = await axiosApiInstance.post(url, payload);
                const list = res?.data?.items ?? res?.data ?? [];
                setItems(list);
                notifySuccess('Jugador eliminado de favoritos');
                return list;
            } catch (error) {
                console.error('Error al eliminar favorito:', error);
                throw error;
            }
        },
        [isUserAuthenticated]
    );

    return {
        favoritos: state?.favoritos?.items ?? [],
        isLoading: state?.favoritos?.isLoading ?? false,
        obtenerFavoritos,
        agregarFavorito,
        eliminarFavorito,
        hasFavoritos: state?.favoritos?.items?.length > 0 ?? false,
    };
}
